import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { inventoryService, jobService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import type {
  MaintenanceRecord,
  CheckoutRecord,
  Document,
} from "../types/database";
import Modal from "../components/Modal";
import EditAssetForm from "../components/EditAssetForm";
import DocumentForm from "../components/DocumentForm";
import MaintenanceRecordForm from "../components/MaintenanceRecordForm";
import JobModal from "../components/JobModal";
import UserName from "../components/UserName";

// Helper function to format currency
function formatGBP(amount: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
}

// Helper function to format date
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function AssetDetail() {
  console.log("Rendering AssetDetail component");
  const { assetId } = useParams<{ assetId: string }>();

  const { currentOrganization } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "details" | "maintenance" | "checkout" | "documents"
  >("details");

  // Log important parameters
  useEffect(() => {
    console.log("AssetDetail - Parameters:", {
      assetId,
      organizationId: currentOrganization?.id,
    });
  }, [assetId, currentOrganization]);

  // Modals state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
  const [editingMaintenanceRecord, setEditingMaintenanceRecord] =
    useState<MaintenanceRecord | null>(null);
  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const {
    data: asset,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["asset", assetId, currentOrganization?.id],
    queryFn: async () => {
      if (!assetId) {
        console.error("Asset ID is missing");
        throw new Error("Asset ID is required");
      }

      try {
        // Add detailed logging to help debug
        console.log(
          `Fetching asset with ID: ${assetId}, orgId: ${currentOrganization?.id}`
        );

        // Use getAssetById to fetch by ID
        const data = await inventoryService.getAssetById(
          assetId,
          currentOrganization?.id
        );

        if (!data) {
          console.error(`Asset not found with ID: ${assetId}`);
          throw new Error(`Asset not found with ID: ${assetId}`);
        }

        console.log(
          "Asset data received:",
          data
            ? {
                id: data.id,
                barcode: data.barcode,
                status: data.status,
                parent_item_id: data.parent_item_id,
                inventory_items: data.inventory_items
                  ? {
                      name: data.inventory_items.name,
                      images: Array.isArray(data.inventory_items.images)
                        ? `${data.inventory_items.images.length} images`
                        : "no images",
                    }
                  : "missing",
              }
            : "not found"
        );

        return data;
      } catch (err) {
        console.error("Error fetching asset:", err);
        throw err;
      }
    },
    enabled: !!assetId && !!currentOrganization?.id,
    retry: 1, // Only retry once to avoid excessive requests on error
    retryDelay: 1000, // Wait 1 second before retrying
  });

  // Fetch job data for checkout records
  const jobIds =
    asset?.checkout_records
      ?.filter((record: any) => record.job)
      .map((record: any) => record.job)
      .filter((id: any): id is string => !!id) || [];

  const { data: jobs = [] } = useQuery({
    queryKey: ["jobs", jobIds, currentOrganization?.id],
    queryFn: async () => {
      if (jobIds.length === 0) return [];
      const jobPromises = jobIds.map((jobId: string) =>
        jobService.getJob(jobId, currentOrganization?.id)
      );
      return Promise.all(jobPromises);
    },
    enabled: jobIds.length > 0 && !!currentOrganization?.id,
  });

  // Log query state changes
  useEffect(() => {
    console.log("Asset query state:", {
      isLoading,
      error: error ? (error as Error).message : null,
      hasData: !!asset,
    });

    if (asset) {
      console.log("Asset data structure:", {
        id: asset.id,
        barcode: asset.barcode,
        inventory_items: asset.inventory_items
          ? `Found (name: ${asset.inventory_items.name})`
          : "Missing inventory_items data",
      });
    }
  }, [isLoading, error, asset]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading asset details...</div>
      </div>
    );
  }

  if (error || !asset) {
    console.error("Error displaying asset:", error);
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Asset Not Found
        </h2>
        <p className="text-gray-600 mb-6">
          The asset with ID <strong>{assetId}</strong> doesn't exist or has been
          removed.
        </p>
        <div className="flex justify-center gap-4">
          <Link
            to={
              currentOrganization
                ? `/org/${currentOrganization.id}/inventory`
                : "/"
            }
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Return to Inventory
          </Link>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Go Back
          </button>
        </div>
        <div className="mt-6 p-4 bg-gray-100 rounded-lg text-left">
          <h3 className="font-bold">Debug Information:</h3>
          <pre className="text-xs overflow-auto mt-2">
            {JSON.stringify(
              {
                assetId,
                organizationId: currentOrganization?.id,
                error: error ? (error as Error).message : "Asset is null",
              },
              null,
              2
            )}
          </pre>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Check if we have the necessary data
  if (!asset.inventory_items) {
    console.error("Missing inventory_items data for asset:", asset.id);
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Incomplete Asset Data
        </h2>
        <p className="text-gray-600 mb-6">
          We found the asset but couldn't load its parent item details.
        </p>
        <div className="flex justify-center gap-4">
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Go Back
          </button>
        </div>
        <div className="mt-6 p-4 bg-gray-100 rounded-lg text-left">
          <h3 className="font-bold">Debug Information:</h3>
          <pre className="text-xs overflow-auto mt-2">
            {JSON.stringify(
              {
                assetId: asset.id,
                barcode: asset.barcode,
                parent_item_id: asset.parent_item_id,
                status: asset.status,
              },
              null,
              2
            )}
          </pre>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "available":
        return "bg-green-100 text-green-800";
      case "checked_out":
        return "bg-blue-100 text-blue-800";
      case "maintenance":
        return "bg-yellow-100 text-yellow-800";
      case "retired":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    refetch();
  };

  const handleDocumentSuccess = () => {
    setEditingDocument(null);
    setIsAddDocumentModalOpen(false);
    refetch();
  };

  const handleMaintenanceSuccess = () => {
    setEditingMaintenanceRecord(null);
    setIsMaintenanceModalOpen(false);
    refetch();
  };

  const openEditDocumentModal = (document: Document) => {
    setEditingDocument(document);
    setIsAddDocumentModalOpen(true);
  };

  const openMaintenanceModal = (record?: MaintenanceRecord) => {
    setEditingMaintenanceRecord(record || null);
    setIsMaintenanceModalOpen(true);
  };

  // Helper functions for checkout history
  const getJobName = (jobId: string | null) => {
    if (!jobId) return "N/A";
    const job = jobs.find((j) => j.id === jobId);
    return job ? job.title : `Job ID: ${jobId}`;
  };

  const openJobModal = (jobId: string) => {
    setSelectedJobId(jobId);
    setIsJobModalOpen(true);
  };

  // Safely access inventory_items data
  const inventoryItemName = asset.inventory_items?.name || "Unknown Item";
  const inventoryItemImages = Array.isArray(asset.inventory_items?.images)
    ? asset.inventory_items.images
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Link
            to={
              currentOrganization
                ? `/org/${currentOrganization.id}/inventory/${asset.parent_item_id}`
                : "/"
            }
            className="text-gray-600 hover:text-primary transition-colors"
            aria-label="Back to item"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{inventoryItemName}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-gray-600">
                Asset: {asset.barcode}
              </span>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                  asset.status
                )}`}
              >
                {asset.status.replace("_", " ").charAt(0).toUpperCase() +
                  asset.status.slice(1).replace("_", " ")}
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            onClick={() => setIsEditModalOpen(true)}
          >
            Edit Asset
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-1 space-y-6">
          {/* Images */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="relative">
              <img
                src={
                  inventoryItemImages[currentImageIndex] ||
                  "https://placehold.co/600x400?text=NO+IMAGE"
                }
                alt={`${inventoryItemName} - ${asset.barcode}`}
                className="w-full h-64 object-contain p-4 cursor-pointer"
                onClick={() => setIsFullScreen(true)}
              />
            </div>

            {/* Thumbnail gallery */}
            {inventoryItemImages.length > 1 && (
              <div className="p-4 border-t border-gray-100">
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {inventoryItemImages.map((image: string, index: number) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                        index === currentImageIndex
                          ? "border-primary"
                          : "border-transparent hover:border-gray-200"
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${inventoryItemName} view ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium text-lg">Documents</h3>
              <button
                onClick={() => setIsAddDocumentModalOpen(true)}
                className="text-xs px-2 py-1 bg-primary text-white rounded hover:bg-primary/90"
              >
                Add Document
              </button>
            </div>
            {asset.documents?.length > 0 ? (
              <div className="space-y-2">
                {asset.documents.map((doc: Document) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center flex-1"
                    >
                      <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center mr-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm font-medium">{doc.name}</div>
                        <div className="text-xs text-gray-500">
                          {doc.type.charAt(0).toUpperCase() + doc.type.slice(1)}
                        </div>
                      </div>
                    </a>
                    <button
                      onClick={() => openEditDocumentModal(doc)}
                      className="text-xs px-2 py-1 text-gray-500 hover:text-gray-700"
                    >
                      Edit
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No documents available</p>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                className={`px-4 py-3 font-medium text-sm ${
                  activeTab === "details"
                    ? "border-b-2 border-primary text-primary"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("details")}
              >
                Details
              </button>
              <button
                className={`px-4 py-3 font-medium text-sm ${
                  activeTab === "maintenance"
                    ? "border-b-2 border-primary text-primary"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("maintenance")}
              >
                Maintenance History
              </button>
              <button
                className={`px-4 py-3 font-medium text-sm ${
                  activeTab === "checkout"
                    ? "border-b-2 border-primary text-primary"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("checkout")}
              >
                Checkout History
              </button>
            </div>

            <div className="p-6">
              {activeTab === "details" && (
                <div className="space-y-6">
                  {/* Asset Information */}
                  <div>
                    <h3 className="text-lg font-medium mb-4">
                      Asset Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">Serial Number</span>
                        <span className="font-medium">
                          {asset.serial_number || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">Barcode</span>
                        <span className="font-medium">{asset.barcode}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">Purchase Date</span>
                        <span className="font-medium">
                          {asset.purchase_date
                            ? new Date(asset.purchase_date).toLocaleDateString(
                                "en-GB"
                              )
                            : "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">Purchase Cost</span>
                        <span className="font-medium">
                          {asset.purchase_cost
                            ? formatGBP(asset.purchase_cost)
                            : "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">Supplier</span>
                        <span className="font-medium">
                          {asset.supplier || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">Order Number</span>
                        <span className="font-medium">
                          {asset.order_number || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">Warranty Until</span>
                        <span className="font-medium">
                          {asset.warranty_end
                            ? new Date(asset.warranty_end).toLocaleDateString(
                                "en-GB"
                              )
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  {asset.notes && (
                    <div>
                      <h3 className="text-lg font-medium mb-2">Notes</h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-700">{asset.notes}</p>
                      </div>
                    </div>
                  )}

                  {/* Creation and Update Info */}
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-4 mt-6">
                      <div>
                        <p className="text-sm text-gray-500">Created By</p>
                        <p className="font-medium">
                          <UserName userId={asset.created_by} />
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatDate(asset.created_at)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Last Updated By</p>
                        <p className="font-medium">
                          <UserName userId={asset.updated_by} />
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatDate(asset.updated_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "maintenance" && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">Maintenance History</h3>
                    <button
                      className="px-3 py-1 bg-primary text-white text-sm rounded-md hover:bg-primary/90"
                      onClick={() => openMaintenanceModal()}
                    >
                      Add Maintenance Record
                    </button>
                  </div>
                  {asset.maintenance_records?.length > 0 ? (
                    <div className="space-y-4">
                      {asset.maintenance_records.map(
                        (record: MaintenanceRecord) => (
                          <div
                            key={record.id}
                            className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`inline-block px-2 py-1 text-xs rounded-full ${
                                    record.type === "repair"
                                      ? "bg-red-100 text-red-800"
                                      : record.type === "inspection"
                                      ? "bg-blue-100 text-blue-800"
                                      : "bg-green-100 text-green-800"
                                  }`}
                                >
                                  {record.type.charAt(0).toUpperCase() +
                                    record.type.slice(1)}
                                </span>
                                <span className="text-sm text-gray-500">
                                  {new Date(record.date).toLocaleDateString(
                                    "en-GB"
                                  )}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                {record.cost !== null && record.cost > 0 && (
                                  <span className="text-sm font-medium">
                                    {formatGBP(record.cost)}
                                  </span>
                                )}
                                <button
                                  onClick={() => openMaintenanceModal(record)}
                                  className="text-xs px-2 py-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                                >
                                  Edit
                                </button>
                              </div>
                            </div>
                            {record.notes && (
                              <p className="text-gray-700 mb-2">
                                {record.notes}
                              </p>
                            )}
                            {record.performed_by && (
                              <div className="text-sm text-gray-500">
                                Performed by: {record.performed_by}
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p className="mb-4">No maintenance records available</p>
                      <button
                        onClick={() => openMaintenanceModal()}
                        className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
                      >
                        Add Your First Record
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "checkout" && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">Checkout History</h3>
                    <div className="flex gap-2">
                      <button
                        className="px-3 py-1 bg-primary text-white text-sm rounded-md hover:bg-primary/90"
                        onClick={() =>
                          console.log("Checkout modal not implemented yet")
                        }
                      >
                        Checkout Asset
                      </button>
                      <button className="text-sm text-primary hover:underline">
                        Export History
                      </button>
                    </div>
                  </div>
                  {asset.checkout_records?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Return Date
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Job
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              By
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {asset.checkout_records.map(
                            (record: CheckoutRecord) => (
                              <tr key={record.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {new Date(
                                    record.checkout_date
                                  ).toLocaleDateString("en-GB")}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {record.return_date ? (
                                    new Date(
                                      record.return_date
                                    ).toLocaleDateString("en-GB")
                                  ) : (
                                    <span className="text-primary font-medium">
                                      Out
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {record.job ? (
                                    <button
                                      onClick={() =>
                                        openJobModal(record.job as string)
                                      }
                                      className="text-primary hover:text-primary/80 hover:underline font-medium"
                                    >
                                      {getJobName(record.job)}
                                    </button>
                                  ) : (
                                    "N/A"
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {record.checked_out_by ? (
                                    <UserName userId={record.checked_out_by} />
                                  ) : (
                                    "Unknown"
                                  )}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No checkout history available
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full screen image modal */}
      {isFullScreen && inventoryItemImages.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
          <button
            onClick={() => setIsFullScreen(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <div className="relative max-w-7xl mx-auto p-4">
            <img
              src={inventoryItemImages[currentImageIndex]}
              alt={`${inventoryItemName} - ${asset.barcode}`}
              className="max-h-[90vh] w-auto mx-auto"
            />
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
              {inventoryItemImages.map((_: string, index: number) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-2 h-2 rounded-full ${
                    index === currentImageIndex ? "bg-white" : "bg-gray-500"
                  }`}
                  aria-label={`View image ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Edit Asset Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Asset"
        size="lg"
      >
        <EditAssetForm
          asset={asset}
          onSuccess={handleEditSuccess}
          onCancel={() => setIsEditModalOpen(false)}
        />
      </Modal>

      {/* Add Document Modal */}
      <Modal
        isOpen={isAddDocumentModalOpen}
        onClose={() => {
          setIsAddDocumentModalOpen(false);
          setEditingDocument(null);
        }}
        title={editingDocument ? "Edit Document" : "Add Document"}
        size="md"
      >
        <DocumentForm
          document={editingDocument || undefined}
          inventoryItemId={asset.parent_item_id}
          assetId={asset.id}
          onSuccess={handleDocumentSuccess}
          onCancel={() => {
            setIsAddDocumentModalOpen(false);
            setEditingDocument(null);
          }}
        />
      </Modal>

      {/* Maintenance Record Modal */}
      <Modal
        isOpen={isMaintenanceModalOpen}
        onClose={() => {
          setIsMaintenanceModalOpen(false);
          setEditingMaintenanceRecord(null);
        }}
        title={
          editingMaintenanceRecord
            ? "Edit Maintenance Record"
            : "Add Maintenance Record"
        }
        size="lg"
      >
        <MaintenanceRecordForm
          assetId={asset.id}
          record={editingMaintenanceRecord || undefined}
          onSuccess={handleMaintenanceSuccess}
          onCancel={() => {
            setIsMaintenanceModalOpen(false);
            setEditingMaintenanceRecord(null);
          }}
        />
      </Modal>

      {/* Job Modal */}
      {selectedJobId && (
        <JobModal
          isOpen={isJobModalOpen}
          onClose={() => {
            setIsJobModalOpen(false);
            setSelectedJobId(null);
          }}
          jobId={selectedJobId}
          organizationId={currentOrganization?.id || ""}
        />
      )}
    </div>
  );
}
